// askSmart-ui/src/pages/TOSP/components/LeaseSnapshot.tsx
import React from "react";
import { formatCurrency, formatDate } from "../format";
import {
  formatSpan,
  isOccupancyActive,
  leaseExpiryFor,
  leasedSpaceLabel,
  occupancyEndDate,
  rentPsfToday,
  spaceEscalation,
  spanBetween,
  Tenant,
  todayISO,
  Unit,
} from "../types";

interface LeaseSnapshotProps {
  tenant: Tenant;
  unitById: (unitId: string) => Unit | undefined;
}

// The lease at a glance, above the sections of the Tenant Details modal: how
// long it has left, and what the tenant holds at what rate.
//
// The same three facts the tenant sees on their own Lease screen, and the same
// pill, so an admin taking a call about it is reading what the caller is. The
// headings that screen carries are dropped — this is a strip above four
// sections, not a document, and "Important Dates" over three dates in an admin
// modal is a line that says nothing.
export default function LeaseSnapshot({ tenant, unitById }: LeaseSnapshotProps) {
  // EVERY lease, ended ones included. A past tenancy has no active lease, and
  // filtering them out left its record with no summary at all — the area it held
  // and what it paid are exactly what somebody opens a closed tenancy to read.
  const leases = tenant.occupancies || [];
  if (leases.length === 0) return null;

  return (
    <div className="tosp-snap">
      {leases.map((lease, index) => {
        const expiry = leaseExpiryFor(lease, lease.rentRenewalNotations || "");
        const span = expiry.state === "unknown" ? null : spanBetween(todayISO(), expiry.on);

        // A tenancy that has ended has no time remaining, so the pill states the
        // date it ended instead of counting from it. Red, which is the module's
        // colour for a lease that is over, and the same pill shape — so the strip
        // reads the same whichever kind of tenancy is open.
        const ended = !isOccupancyActive(lease);

        return (
          <div key={lease.id} className="tosp-snap-lease">
            <div className="tosp-snap-head">
              <span className="tosp-snap-label">
                {/* Named only when there is more than one to tell apart. */}
                {leases.length > 1
                  ? `Lease ${index + 1} — ${ended ? "Exit Date" : "Time Remaining"}`
                  : ended
                    ? "Exit Date"
                    : "Time Remaining"}
              </span>
              {ended ? (
                <span
                  className="tosp-lease-flag tosp-lease-expired"
                  title="The last day of the lease — this tenancy has ended"
                >
                  {/* formatDate renders an em dash for an empty string, so a
                      lease with no date to show says so rather than blank. */}
                  {formatDate(occupancyEndDate(lease) || "")}
                </span>
              ) : expiry.state === "unknown" || !span ? (
                <span className="tosp-progress-none">—</span>
              ) : (
                // The overview column's pill, unchanged: same four states, same
                // colours, and once expired the count runs the other way, which
                // the leading + says.
                <span
                  className={`tosp-lease-flag tosp-lease-${expiry.state}`}
                  title={
                    expiry.state === "expired"
                      ? `Lease ended ${expiry.on}`
                      : `Lease ends ${expiry.on}`
                  }
                >
                  {expiry.state === "expired" ? `+${formatSpan(span)}` : formatSpan(span)}
                </span>
              )}
            </div>

            <table className="tosp-snap-table">
              <thead>
                <tr>
                  <th scope="col">Area</th>
                  <th scope="col">Sqft</th>
                  <th scope="col">Rent psf</th>
                </tr>
              </thead>
              <tbody>
                {lease.leasedSpaces.map((space) => {
                  const unit = space.unitId ? unitById(space.unitId) : undefined;
                  // The rate in force TODAY — the same figure the space card
                  // below labels "Rent PSF Current". Not rentPsfCurrent, which
                  // despite its name is the year's pre-step figure.
                  const psf = rentPsfToday(spaceEscalation(lease, space));
                  return (
                    <tr key={space.id}>
                      <td>{leasedSpaceLabel(space, unit)}</td>
                      <td>
                        {unit?.occupancySqFt == null
                          ? "—"
                          : unit.occupancySqFt.toLocaleString()}
                      </td>
                      <td>{psf === null ? "—" : formatCurrency(psf)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        );
      })}
    </div>
  );
}
